import { useState } from "react";
import { useLeaveBalance } from "../hooks/useLeaveBalance";
import { motion, AnimatePresence } from "framer-motion";

function Section({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        height: {
          type: "spring",
          stiffness: 250,
          damping: 28,
        },
        opacity: {
          duration: 0.2,
        },
      }}
      className="border-b border-slate-100 py-3 first:pt-0 last:border-b-0"
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between py-1 text-left text-sm font-semibold text-slate-700"
      >
        {title}

        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{
            height: {
              type: "spring",
              stiffness: 250,
              damping: 28,
            },
            opacity: {
              duration: 0.2,
            },
          }}
          className="text-slate-400"
        >
          ⌄
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              height: {
                type: "spring",
                stiffness: 250,
                damping: 28,
              },
              opacity: {
                duration: 0.2,
              },
            }}
            className="overflow-hidden"
          >
            <motion.div
              initial={{ y: -8 }}
              animate={{ y: 0 }}
              exit={{ y: -8 }}
              transition={{
                height: {
                  type: "spring",
                  stiffness: 250,
                  damping: 28,
                },
                opacity: {
                  duration: 0.2,
                },
              }}
              className="mt-3"
            >
              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const StatCard = ({ label, value }: { label: string; value: number }) => {
  return (
    <div className="p-2 rounded-lg bg-gray-100">
      <h1 className="text-sm font-medium ">{label}</h1>
      <p className="text-sm text-muted-foreground">{value}</p>
    </div>
  );
};

const LeaveBalance = ({ employeeId }: { employeeId: number }) => {
  const { data: leaveBalance, isLoading } = useLeaveBalance(employeeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <span className="loading loading-ring loading-md"></span>
      </div>
    );
  }

  return (
    <div>
      {leaveBalance ? (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{
              duration: 0.4,
              ease: "easeOut",
            }}
          >
            <div className="bg-white rounded-xl border p-3">
              <Section title="Sick Leave">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <StatCard
                    label={"Leaves Used"}
                    value={leaveBalance.balances.SL.used}
                  />
                  <StatCard
                    label={"Remaining Leaves"}
                    value={leaveBalance.balances.SL.remaining}
                  />
                </div>

                <div className="space-y-2">
                  <StatCard
                    label={"Max Leaves Per Application"}
                    value={leaveBalance.balances.SL.maxPerApplication}
                  />
                  <StatCard
                    label={"Leave Quota"}
                    value={leaveBalance.balances.SL.quota}
                  />
                  <StatCard
                    label={"Advance Notice Days"}
                    value={leaveBalance.balances.SL.advanceNoticeDays}
                  />
                </div>
              </Section>
              {!leaveBalance.under_probation && (
                <>
                  <Section title="Casual Leave">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <StatCard
                        label={"Leaves Used"}
                        value={leaveBalance.balances.CL.used}
                      />
                      <StatCard
                        label={"Remaining Leaves"}
                        value={leaveBalance.balances.CL.remaining}
                      />
                    </div>

                    <div className="space-y-2">
                      <StatCard
                        label={"Max Leaves Per Application"}
                        value={leaveBalance.balances.CL.maxPerApplication}
                      />
                      <StatCard
                        label={"Leave Quota"}
                        value={leaveBalance.balances.CL.quota}
                      />
                      <StatCard
                        label={"Advance Notice Days"}
                        value={leaveBalance.balances.CL.advanceNoticeDays}
                      />
                    </div>
                  </Section>

                  <Section title="Planned Leave">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <StatCard
                        label={"Leaves Used"}
                        value={leaveBalance.balances.PLL.used}
                      />
                      <StatCard
                        label={"Remaining Leaves"}
                        value={leaveBalance.balances.PLL.remaining}
                      />
                    </div>

                    <div className="space-y-2">
                      <StatCard
                        label={"Max Leaves Per Application"}
                        value={leaveBalance.balances.PLL.maxPerApplication}
                      />
                      <StatCard
                        label={"Leave Quota"}
                        value={leaveBalance.balances.PLL.quota}
                      />
                      <StatCard
                        label={"Advance Notice Days"}
                        value={leaveBalance.balances.PLL.advanceNoticeDays}
                      />
                    </div>
                  </Section>

                  {leaveBalance.balances.MTL && (
                    <Section title="Maternity Leave">
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <StatCard
                          label={"Leaves Used"}
                          value={leaveBalance.balances.MTL.used}
                        />
                        <StatCard
                          label={"Remaining Leaves"}
                          value={leaveBalance.balances.MTL.remaining}
                        />
                      </div>
                      <div className="space-y-2">
                        <StatCard
                          label={"Leave Quota"}
                          value={leaveBalance.balances.MTL.quota}
                        />
                        <StatCard
                          label={"Advance Notice Days"}
                          value={leaveBalance.balances.MTL.advanceNoticeDays}
                        />
                      </div>
                    </Section>
                  )}

                  {leaveBalance.balances.PTL && (
                    <Section title="Paternity Leave">
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <StatCard
                          label={"Leaves Used"}
                          value={leaveBalance.balances.PTL.used}
                        />
                        <StatCard
                          label={"Remaining Leaves"}
                          value={leaveBalance.balances.PTL.remaining}
                        />
                      </div>
                      <div className="space-y-2">
                        <StatCard
                          label={"Max Leaves Per Application"}
                          value={leaveBalance.balances.PTL.maxPerApplication}
                        />
                        <StatCard
                          label={"Leave Quota"}
                          value={leaveBalance.balances.PTL.quota}
                        />
                        <StatCard
                          label={"Advance Notice Days"}
                          value={leaveBalance.balances.PTL.advanceNoticeDays}
                        />
                      </div>
                    </Section>
                  )}
                </>
              )}

              <Section title="Leave Without Pay">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <StatCard
                    label={"Leaves Used"}
                    value={leaveBalance.balances.LWP.used}
                  />
                  <StatCard
                    label={"Remaining Leaves"}
                    value={leaveBalance.balances.LWP.remaining}
                  />
                </div>
                <StatCard
                  label={"Leave Quota"}
                  value={leaveBalance.balances.LWP.quota}
                />
              </Section>
            </div>
          </motion.div>
        </AnimatePresence>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{
              duration: 0.4,
              ease: "easeOut",
            }}
          >
            <h2>No Leave Balance</h2>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default LeaveBalance;
